import type { EpisodeSummary, NPCReaction } from '../services/GameApiService';

interface RecapDisplayProps {
  characterName: string;
  summary: EpisodeSummary;
  npcReaction?: NPCReaction;
  episodeNumber?: number;
  /** Show loading indicator for the NPC reaction section */
  isLoadingReaction?: boolean;
  /** Bridge into the next encounter — rendered as a continuation of the recap prose. */
  bridgeText?: string | null;
}

export default function RecapDisplay({
  characterName,
  summary,
  npcReaction,
  episodeNumber,
  isLoadingReaction = false,
  bridgeText = null,
}: RecapDisplayProps) {
  return (
    <div className="bg-white dark:bg-white/10 dark:backdrop-blur-md dark:border dark:border-white/15 rounded-2xl shadow-lg dark:shadow-none p-6 md:p-8">
      {/* Episode Header */}
      <div className="mb-6">
        {episodeNumber !== undefined && (
          <span className="text-sm font-medium text-primary uppercase tracking-wider">
            Episódio {episodeNumber}
          </span>
        )}
        <h2 className="text-2xl font-bold text-dark dark:text-white mt-1">{summary.title}</h2>
        <p className="text-gray-500 dark:text-white/60 mt-1">
          Conversa com <span className="font-semibold">{characterName}</span>
        </p>
      </div>

      {/* Narrative Recap, followed by the bridge into the next encounter as one continuous chapter */}
      <div className="prose prose-lg dark:prose-invert max-w-none mb-6">
        <p className="text-gray-700 dark:text-white/80 leading-relaxed whitespace-pre-line">
          {summary.narrativeRecap}
        </p>
        {bridgeText && bridgeText.trim() !== '' && (
          <p className="text-gray-700 dark:text-white/80 leading-relaxed whitespace-pre-line mt-4">
            {bridgeText}
          </p>
        )}
      </div>

      {/* Key Moments */}
      {summary.keyMoments.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-dark dark:text-white mb-3">
            Momentos Chave
          </h3>
          <ul className="space-y-2">
            {summary.keyMoments.map((moment, index) => (
              <li key={index} className="flex items-start">
                <span className="text-primary mr-2">•</span>
                <span className="text-gray-700 dark:text-white/80">{moment}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* NPC Reaction Loading State */}
      {isLoadingReaction && !npcReaction && (
        <div className="bg-gray-50 dark:bg-white/5 dark:border dark:border-white/10 rounded-xl p-5 mt-6">
          <h3 className="text-lg font-semibold text-dark dark:text-white mb-3">
            Reação de {characterName}
          </h3>
          <div className="flex items-center gap-3 text-gray-500 dark:text-white/60">
            <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full"></div>
            <span>A analisar a reação do personagem...</span>
          </div>
        </div>
      )}

      {/* NPC Reaction */}
      {npcReaction && (
        <div className="bg-gray-50 dark:bg-white/5 dark:border dark:border-white/10 rounded-xl p-5 mt-6">
          <h3 className="text-lg font-semibold text-dark dark:text-white mb-3">
            Reação de {characterName}
          </h3>

          {/* Relationship Score */}
          <div className="mb-4">
            <div className="flex justify-between mb-1">
              <span className="text-sm text-gray-600 dark:text-white/60">Relação</span>
              <span className="text-sm font-medium dark:text-white">
                {npcReaction.relationshipScore}%
              </span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-white/10 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-primary to-accent rounded-full h-2 transition-all duration-500"
                style={{ width: `${npcReaction.relationshipScore}%` }}
              />
            </div>
          </div>

          {/* Gossip */}
          {npcReaction.gossip && (
            <div className="mt-4 p-4 bg-white dark:bg-white/10 rounded-lg border-l-4 border-primary">
              <p className="text-sm text-gray-500 dark:text-white/60 mb-1">O que diria a outros:</p>
              <p className="text-gray-700 dark:text-white/80 italic">"{npcReaction.gossip}"</p>
            </div>
          )}

          {/* Will Help */}
          <div className="mt-4 text-sm">
            <span className="text-gray-600 dark:text-white/60">Disposição para ajudar: </span>
            <span className={`font-medium ${
              npcReaction.willHelpInFuture === 'yes'
                ? 'text-green-600 dark:text-green-400'
                : npcReaction.willHelpInFuture === 'no'
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-yellow-600 dark:text-yellow-400'
            }`}>
              {npcReaction.willHelpInFuture === 'yes'
                ? 'Sim'
                : npcReaction.willHelpInFuture === 'no'
                  ? 'Não'
                  : 'Depende'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
